/**
 * "End of passage set" interstitial, shown after the student answers the
 * last question of a passage group and chooses to advance. Renders 1:1
 * with the official SHSAT printable test (page 11):
 *
 *   There are no more questions for this passage set.
 *   Use the review button [Review ☰] to return to any questions about
 *   the passage you have just read.
 *   Once you select the blue arrow [← →] at the top of this screen, you
 *   will NOT be able to return to any questions about this passage.
 *
 * Presentational only — renders inside the module body while the live
 * top bar (real back / Review / blue forward) stays above it. Used by
 * BOTH the adaptive flow (AdaptiveModule) and the legacy locked flow
 * (Module/index.tsx), so it owns no navigation logic; the inline Review
 * pill and arrow glyphs are static representations matching the PDF.
 */

import React from 'react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { PiListBulletsBold } from 'react-icons/pi';

/** Inline, non-interactive copy of the toolbar Review pill — purely a
 * visual reference inside the sentence, matching the printed test. */
function ReviewGlyph(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        border: '1px solid #bdbdbd',
        borderRadius: 4,
        padding: '2px 8px',
        margin: '0 4px',
        fontSize: '0.9em',
        fontWeight: 500,
        background: '#f5f5f5',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
      }}
    >
      Review <PiListBulletsBold />
    </span>
  );
}

/** Inline, non-interactive copy of the back/forward arrow pair, with the
 * forward arrow highlighted blue to match the printed test. */
function ArrowGlyph(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        margin: '0 4px',
        verticalAlign: 'middle',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #bdbdbd',
          borderRadius: '4px 0 0 4px',
          padding: '4px 8px',
          background: '#f5f5f5',
          color: '#676767',
          fontSize: '0.85em',
        }}
      >
        <FaArrowLeft />
      </span>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #2f6fd1',
          borderRadius: '0 4px 4px 0',
          padding: '4px 10px',
          background: '#2f6fd1',
          color: '#fff',
          fontSize: '0.85em',
        }}
      >
        <FaArrowRight />
      </span>
    </span>
  );
}

export function PassageInterstitial(): JSX.Element {
  return (
    <div className="digitalSHSATModule__passageInterstitial">
      <div className="digitalSHSATModule__passageInterstitialCard">
        <h2 className="digitalSHSATModule__passageInterstitialHeading">
          There are no more questions for this passage set.
        </h2>
        <p className="digitalSHSATModule__passageInterstitialText">
          Use the review button <ReviewGlyph /> to return to any
          questions about the passage you have just read.
        </p>
        <p className="digitalSHSATModule__passageInterstitialText">
          Once you select the blue arrow <ArrowGlyph /> at the top of this
          screen, you will <strong>not</strong> be able to return to any
          questions about this passage.
        </p>
      </div>
    </div>
  );
}

export default PassageInterstitial;
